import { AlertCircle } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Github } from '@/components/icons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { HackerOnboardsShow } from '@/types';
import { VenueWelcome } from './components/VenueWelcome';

// /hacker/:publicId/onboard — onboard-welcome.tsx. The `error` the Next component
// assembled from `accountMismatch` and `?error=` is decided server-side and arrives whole.
export default function Show({
  hacker,
  eventName,
  error,
  venue,
}: HackerOnboardsShow) {
  const [isLoading, setIsLoading] = useState(false);
  const [showVenueWelcome, setShowVenueWelcome] = useState(
    Boolean(venue) && !error,
  );

  const welcomeMessage =
    hacker.gender === 'female' ? 'Bienvenida' : 'Bienvenido';
  const githubUsername = hacker.github?.split('/').pop() || '';

  useEffect(() => {
    if (error) {
      const errorMessages: Record<string, string> = {
        unauthorized: 'No estás autorizado para acceder a esta cuenta.',
        unable_to_link_account: `La cuenta de GitHub que intentaste conectar no coincide con @${githubUsername}. Por favor, inicia sesión con la cuenta correcta.`,
      };

      const message =
        errorMessages[error] ||
        'Ocurrió un error al conectar tu cuenta de GitHub.';
      toast.error(message);
    }
  }, [error, githubUsername]);

  // The same call the better-auth client made: the endpoint hands back the URL that
  // starts the OAuth flow and remembers where a failure should land.
  const handleGithubConnect = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'github',
          callbackURL: `/hacker/${hacker.publicId}/onboard`,
          errorCallbackURL: `/hacker/${hacker.publicId}/onboard?error=unauthorized`,
        }),
      });
      const body = (await response.json()) as { url?: string };

      if (!body.url) {
        throw new Error('Sign-in failed');
      }

      window.location.href = body.url;
    } catch (connectError) {
      toast.error('Error al conectar con GitHub');
      console.error(connectError);
      setIsLoading(false);
    }
  };

  if (showVenueWelcome && venue) {
    return (
      <OnboardShell>
        <VenueWelcome
          githubUsername={githubUsername}
          venue={venue}
          onContinue={() => setShowVenueWelcome(false)}
        />
      </OnboardShell>
    );
  }

  return (
    <OnboardShell>
      <div className="min-h-screen bg-background">
      <div className="container mx-auto flex flex-col px-4 py-12 md:py-16">
        {/* Platanus logo footer */}
        <div className="order-last mt-16 flex justify-center">
          <div
            className="aspect-[576/112] h-7 w-auto sm:h-9 md:h-10"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              maskImage: 'url(/assets/logos/platanus.svg)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskImage: 'url(/assets/logos/platanus.svg)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
            }}
          />
        </div>

        {/* Main content */}
        <div className="mx-auto w-full max-w-4xl space-y-8">
          {/* Title */}
          <div className="text-center">
            <h1 className="inline-block font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
              {welcomeMessage}{' '}
              <span className="bg-primary px-2 py-1 text-background">
                {hacker.fullName}
              </span>
            </h1>
            <p className="mt-4 font-mono text-primary/70 text-sm">
              Bienvenid{hacker.gender === 'female' ? 'a' : 'o'} a{' '}
              {eventName ?? 'Platanus Hack'}
            </p>
          </div>

          {/* Onboarding Card */}
          <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
            <div className="space-y-6">
              {/* Header */}
              <div className="border-primary/20 border-b pb-4">
                <h2 className="font-bold text-2xl text-primary sm:text-3xl">
                  Conecta tu cuenta
                </h2>
                <p className="mt-2 font-mono text-primary/80 text-sm">
                  Antes de que puedas ingresar al evento, debemos conectar
                  algunas cuentas y rellenar un poco de información adicional.
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error de autenticación</AlertTitle>
                  <AlertDescription>
                    {error === 'unable_to_link_account'
                      ? `La cuenta de GitHub que intentaste conectar no coincide con @${githubUsername}. Por favor, inicia sesión con la cuenta correcta.`
                      : error === 'unauthorized'
                        ? 'No estás autorizado para acceder a esta cuenta.'
                        : 'Ocurrió un error al conectar tu cuenta de GitHub. Inténtalo nuevamente.'}
                  </AlertDescription>
                </Alert>
              )}

              {/* GitHub Connection */}
              <div className="bg-primary/10 p-6">
                <p className="mb-4 font-bold text-primary">
                  Comencemos por agregar tu cuenta de GitHub
                </p>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    {hacker.github && (
                      <img
                        src={`https://github.com/${githubUsername}.png`}
                        alt={`${githubUsername} avatar`}
                        width={64}
                        height={64}
                        className="rounded-full border-2 border-primary"
                      />
                    )}
                    <div className="flex flex-col gap-1">
                      <p className="font-bold font-mono text-lg text-primary">
                        @{githubUsername || 'GitHub no configurado'}
                      </p>
                      {hacker.github && (
                        <a
                          href={hacker.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-primary/70 text-sm transition-colors hover:text-primary hover:underline"
                        >
                          {hacker.github}
                        </a>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={handleGithubConnect}
                    disabled={isLoading || !hacker.github}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    <Github className="mr-2 h-5 w-5" />
                    {isLoading ? 'Conectando...' : 'Conectar'}
                  </Button>
                </div>

                {!hacker.github && (
                  <p className="mt-4 text-destructive text-sm">
                    No tienes una cuenta de GitHub configurada. Por favor
                    contacta a los organizadores.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </OnboardShell>
  );
}

// app/(hack)/hacker/[publicId]/onboard/layout.tsx.
function OnboardShell({ children }: { children: ReactNode }) {
  return (
    <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
      {children}
    </div>
  );
}
